/**
 * Random Sticker Service
 *
 * A centralized service for generating random stickers with consistent filtering,
 * caching, and duplicate avoidance across the entire application.
 *
 * Features:
 * - Handles its own data loading with caching
 * - Supports both single and multiple sticker requests
 * - Tracks shown stickers globally to avoid duplicates across pages
 * - Filters for MICRO size and smaller only
 * - Weighted random selection (70% MICRO preference)
 * - Fallback to logo if no stickers found
 * - Future-proofed for different modes
 *
 * Usage:
 * - Single sticker: randomStickerService.getRandomSticker()
 * - Multiple stickers: randomStickerService.getRandomStickers(3)
 * - Reset tracking: randomStickerService.resetShownStickers()
 */

import databaseService from './databaseService';
import intelligentLoadingService from './intelligentLoadingService';

import logger from '../utils/logger.js';
class RandomStickerService {
  constructor() {
    // Cache for loaded stickers
    this.cachedStickers = [];
    this.isLoading = false;
    this.lastLoadTime = null;
    this.cacheExpiry = 5 * 60 * 1000; // 5 minutes

    // Default configuration
    this.defaultConfig = {
      excludedListingIds: [1327319473, 855766109], // Bigfoot Canoe, additional exclusions
      preferredSizes: ['MICRO', 'NANO', 'PIXEL', 'ATOM'],
      microWeight: 0.7, // 70% chance for MICRO
      useSmartLoading: true,
      fallbackToLogo: true,
    };

    // Logo first design data (shown first on page load)
    this.logoFirstDesign = {
      id: 'logo-first-design',
      title: 'The Best Micro Decals In The System',
      svgPath: '/assets/svgs/clothedEye_BMDITS.svg',
      size: 'MEDIUM',
      colorIndex: 0, // Always black
      width: 200,
      height: 120,
      isFirstDesign: true,
    };

    // Logo fallback data (when no stickers found)
    this.logoFallback = {
      id: 'logo-fallback',
      title: 'ClothedEye Logo',
      svgPath: '/assets/svgs/clothedEye.svg',
      size: 'MEDIUM',
      colorIndex: 0,
      width: 200,
      height: 120,
      isFallback: true,
    };

    // Initialize session storage tracking
    this.initializeSessionTracking();
  }

  /**
   * Initialize session storage tracking for persistent variety across pages
   */
  initializeSessionTracking() {
    const sessionKey = 'clothedEye_randomStickers';

    try {
      const saved = sessionStorage.getItem(sessionKey);
      if (saved) {
        const data = JSON.parse(saved);
        this.shownStickers = new Set(data.shownStickers || []);
        this.stickerUsageCount = new Map(data.stickerUsageCount || []);
        this.logoShownThisSession = data.logoShownThisSession || false;
      } else {
        // First time - initialize fresh
        this.shownStickers = new Set();
        this.stickerUsageCount = new Map();
        this.logoShownThisSession = false;
      }
    } catch (error) {
      logger.warn('Failed to load session storage data, starting fresh:', error);
      this.shownStickers = new Set();
      this.stickerUsageCount = new Map();
      this.logoShownThisSession = false;
    }
  }

  /**
   * Save current tracking state to session storage
   */
  saveSessionTracking() {
    const sessionKey = 'clothedEye_randomStickers';

    try {
      const data = {
        shownStickers: Array.from(this.shownStickers),
        stickerUsageCount: Array.from(this.stickerUsageCount.entries()),
        logoShownThisSession: this.logoShownThisSession,
      };
      sessionStorage.setItem(sessionKey, JSON.stringify(data));
    } catch (error) {
      logger.warn('Failed to save session storage data:', error);
    }
  }

  /**
   * Load stickers from database with caching
   */
  async loadStickers(config = {}) {
    const now = Date.now();

    // Return cached data if still valid
    if (
      this.cachedStickers.length > 0 &&
      this.lastLoadTime &&
      now - this.lastLoadTime < this.cacheExpiry
    ) {
      return this.cachedStickers;
    }

    // Prevent multiple simultaneous loads
    if (this.isLoading) {
      return new Promise((resolve) => {
        const checkLoaded = () => {
          if (!this.isLoading) {
            resolve(this.cachedStickers);
          } else {
            setTimeout(checkLoaded, 100);
          }
        };
        checkLoaded();
      });
    }

    this.isLoading = true;

    try {
      let stickers = [];
      const finalConfig = { ...this.defaultConfig, ...config };

      if (finalConfig.useSmartLoading) {
        const smartData = await intelligentLoadingService.loadCriticalFirst();
        stickers = smartData.heroStickers || [];
      } else {
        const response = await databaseService.getUnifiedStickers({
          hasSvgPath: true,
          limit: 200,
        });
        stickers = response.data || [];
      }

      // Filter and process stickers
      const processedStickers = this.processStickers(stickers, finalConfig);

      this.cachedStickers = processedStickers;
      this.lastLoadTime = now;

      // logger.log(`✅ RandomStickerService: Loaded ${processedStickers.length} stickers`);
      return processedStickers;
    } catch (error) {
      console.error('❌ RandomStickerService: Failed to load stickers:', error);
      return [];
    } finally {
      this.isLoading = false;
    }
  }

  /**
   * Process raw stickers with filtering and size validation
   */
  processStickers(rawStickers, config) {
    return rawStickers
      .filter((sticker) => {
        // Must have SVG path
        if (!sticker.svgPath || sticker.svgPath.trim() === '') {
          return false;
        }

        // Must not be excluded
        const listingId = sticker.etsyListingId || sticker.listing_id || sticker.id;
        if (config.excludedListingIds.includes(Number(listingId))) {
          return false;
        }

        return true;
      })
      .map((sticker) => {
        // Process available sizes
        let availableSizes = [];

        if (sticker.dimensions) {
          try {
            const dims =
              typeof sticker.dimensions === 'string'
                ? JSON.parse(sticker.dimensions)
                : sticker.dimensions;
            if (dims && typeof dims === 'object') {
              availableSizes = Object.keys(dims);
            }
          } catch (e) {
            logger.warn('Error parsing dimensions for', sticker.title, e);
            availableSizes = ['MICRO']; // Safe fallback
          }
        }

        // Filter to only allowed sizes (MICRO and smaller)
        const allowedSizes = availableSizes.filter((size) => {
          const sizeStr = String(size).toUpperCase();
          return config.preferredSizes.some((preferredSize) =>
            sizeStr.includes(preferredSize.toUpperCase())
          );
        });

        return {
          ...sticker,
          availableSizes: allowedSizes.length > 0 ? allowedSizes : ['MICRO'],
        };
      })
      .filter((sticker) => sticker.availableSizes.length > 0);
  }

  /**
   * Get weighted random size (70% MICRO preference)
   */
  getWeightedRandomSize(availableSizes) {
    if (!availableSizes || availableSizes.length === 0) return 'MICRO';

    const cleanSizes = availableSizes.map((size) => {
      const baseSizeName = String(size).match(/^(\w+)/);
      return baseSizeName ? baseSizeName[1].toUpperCase() : String(size).toUpperCase();
    });

    const uniqueSizes = [...new Set(cleanSizes)];
    const microIndex = uniqueSizes.indexOf('MICRO');

    if (microIndex !== -1) {
      const random = Math.random();
      if (random < this.defaultConfig.microWeight) {
        return 'MICRO';
      } else {
        const otherSizes = uniqueSizes.filter((size) => size !== 'MICRO');
        return otherSizes.length > 0
          ? otherSizes[Math.floor(Math.random() * otherSizes.length)]
          : 'MICRO';
      }
    }

    return uniqueSizes[Math.floor(Math.random() * uniqueSizes.length)];
  }

  /**
   * Get weighted random color (35% black preference)
   */
  getWeightedRandomColor() {
    const goodColors = [0, 3, 4, 7, 8, 10, 11, 12, 13]; // Black, Red, Orange, Green, Forest Green, Sky Blue, Blue, Purple, Violet
    const random = Math.random();

    if (random < 0.35) {
      return 0; // Black
    } else {
      const otherColors = goodColors.filter((color) => color !== 0);
      return otherColors[Math.floor(Math.random() * otherColors.length)];
    }
  }

  /**
   * Calculate dimensions for a sticker
   */
  calculateDimensions(sticker, selectedSize) {
    let width = 200;
    let height = 120;

    if (sticker && sticker.dimensions) {
      try {
        const dimensions =
          typeof sticker.dimensions === 'string'
            ? JSON.parse(sticker.dimensions)
            : sticker.dimensions;

        let dims = dimensions[selectedSize];
        if (!dims) {
          const possibleKeys = Object.keys(dimensions).filter((key) =>
            key.toUpperCase().startsWith(selectedSize.toUpperCase())
          );
          if (possibleKeys.length > 0) {
            dims = dimensions[possibleKeys[0]];
          }
        }

        if (dims && dims.width && dims.height) {
          // Heuristic: detect unit (inches vs pixels)
          // If both dimensions are reasonably small, treat as inches; otherwise as pixels
          const isLikelyInches = dims.width <= 20 && dims.height <= 20;
          if (isLikelyInches) {
            // Convert inches to pixels at 96 DPI + padding
            width = Math.round(dims.width * 96) + 48;
            height = Math.round(dims.height * 96) + 48;
          } else {
            // Assume already in pixels; add minimal padding only
            width = Math.round(dims.width) + 24;
            height = Math.round(dims.height) + 24;
          }
        }
      } catch (error) {
        logger.warn('Error parsing dimensions for', sticker.title, error);
      }
    }

    return { width, height };
  }

  /**
   * Get a single random sticker with exhaustive variety (no repeats until all designs shown)
   */
  async getRandomSticker(config = {}) {
    // Show logo as first design if not shown this session
    if (!this.logoShownThisSession) {
      this.logoShownThisSession = true;
      this.saveSessionTracking();
      return this.logoFirstDesign;
    }

    const stickers = await this.loadStickers(config);
    if (stickers.length === 0) {
      return this.logoFallback;
    }

    // Get listing ID for each sticker for proper tracking
    const stickersWithIds = stickers
      .map((sticker) => ({
        ...sticker,
        listingId: sticker.etsyListingId || sticker.id || sticker.listing_id,
      }))
      .filter((sticker) => sticker.listingId); // Only stickers with valid IDs

    if (stickersWithIds.length === 0) {
      return this.logoFallback;
    }

    // EXHAUSTIVE VARIETY: Get stickers that have NEVER been shown
    let availableStickers = stickersWithIds.filter((sticker) => {
      const currentCount = this.stickerUsageCount.get(sticker.listingId) || 0;
      return currentCount === 0; // Only never-shown designs
    });

    // If ALL designs have been shown, reset tracking and start over
    if (availableStickers.length === 0) {
      logger.log(
        '🎯 RandomStickerService: All designs shown! Resetting for fresh variety cycle...'
      );
      this.resetTrackingForNewCycle();
      this.saveSessionTracking();

      // Now all stickers are available again
      availableStickers = stickersWithIds;
    }

    if (availableStickers.length === 0) {
      return this.logoFallback;
    }

    const selectedSticker = availableStickers[Math.floor(Math.random() * availableStickers.length)];
    const selectedSize = this.getWeightedRandomSize(selectedSticker.availableSizes);
    const selectedColorIndex = this.getWeightedRandomColor();
    const { width, height } = this.calculateDimensions(selectedSticker, selectedSize);

    // Track usage by listing ID (design-based, not size/color based)
    const currentCount = this.stickerUsageCount.get(selectedSticker.listingId) || 0;
    this.stickerUsageCount.set(selectedSticker.listingId, currentCount + 1);
    this.shownStickers.add(selectedSticker.listingId);

    // Save session tracking after each selection
    this.saveSessionTracking();

    logger.log(
      `🎨 RandomStickerService: Selected ${selectedSticker.title} (ID: ${selectedSticker.listingId}), Shown: ${this.shownStickers.size}/${stickersWithIds.length} designs`
    );

    return {
      id: selectedSticker.listingId,
      title: selectedSticker.title,
      svgPath: selectedSticker.svgPath,
      size: selectedSize,
      colorIndex: selectedColorIndex,
      width,
      height,
      stickerData: {
        ...selectedSticker,
        id: selectedSticker.listingId,
        etsyListingId: selectedSticker.listingId,
      },
    };
  }

  /**
   * Reset tracking for a new variety cycle (when all designs have been shown)
   */
  resetTrackingForNewCycle() {
    this.shownStickers.clear();
    this.stickerUsageCount.clear();
    // Don't reset logo flag - it should only show once per session
  }

  /**
   * Get multiple random stickers with exhaustive variety
   */
  async getRandomStickers(count = 3, config = {}) {
    const stickers = [];

    // If this is the first call this session, include logo as first sticker
    if (!this.logoShownThisSession) {
      this.logoShownThisSession = true;
      this.saveSessionTracking();
      stickers.push(this.logoFirstDesign);
      count--; // Reduce count since we added the logo
    }

    // Get remaining random stickers
    for (let i = 0; i < count; i++) {
      const sticker = await this.getRandomSticker(config);
      // Avoid duplicates within the same batch
      if (!stickers.find((s) => s.id === sticker.id)) {
        stickers.push(sticker);
      } else {
        // If we get a duplicate, try one more time
        const retrySticker = await this.getRandomSticker(config);
        stickers.push(retrySticker);
      }
    }

    return stickers;
  }

  /**
   * Reset shown stickers tracking - DEPRECATED: Use manual reset only
   * This method is now used only for manual resets, not automatic component resets
   */
  resetShownStickers() {
    this.shownStickers.clear();
    this.stickerUsageCount.clear();
    this.logoShownThisSession = false; // Reset logo tracking so it shows again
    this.saveSessionTracking();
    logger.log('🔄 RandomStickerService: Manual reset of shown stickers tracking');
  }

  /**
   * Clear cache and reload stickers
   */
  async refreshStickers(config = {}) {
    this.cachedStickers = [];
    this.lastLoadTime = null;
    return await this.loadStickers(config);
  }

  /**
   * Get service statistics
   */
  getStats() {
    return {
      cachedStickersCount: this.cachedStickers.length,
      shownStickersCount: this.shownStickers.size,
      logoShownThisSession: this.logoShownThisSession,
      isLoading: this.isLoading,
      lastLoadTime: this.lastLoadTime,
      cacheAge: this.lastLoadTime ? Date.now() - this.lastLoadTime : null,
      trackingMethod: 'etsyListingId', // Now tracking by listing ID instead of title
      varietyStatus:
        this.shownStickers.size > 0
          ? `${this.shownStickers.size} unique designs shown`
          : 'No designs shown yet',
    };
  }

  /**
   * Debug method: Get list of shown sticker IDs for testing
   */
  getShownStickerIds() {
    return Array.from(this.shownStickers);
  }

  /**
   * Debug method: Test exhaustive variety by simulating multiple requests
   */
  async testExhaustiveVariety(testCount = 10) {
    logger.log(`🧪 Testing exhaustive variety with ${testCount} requests...`);
    const results = [];

    for (let i = 0; i < testCount; i++) {
      const sticker = await this.getRandomSticker();
      results.push({
        request: i + 1,
        stickerId: sticker.id,
        title: sticker.title,
        totalShown: this.shownStickers.size,
      });
    }

    logger.log('🧪 Test Results:', results);
    logger.log(`🎯 Final Stats: ${this.shownStickers.size} unique designs shown`);
    return results;
  }
}

// Create singleton instance
const randomStickerService = new RandomStickerService();

export default randomStickerService;
